<script lang="ts">
	import { evaluate } from '$lib/utils/storyblok/formula_parser';
	import { renderRichText, type ISbRichtext } from '@storyblok/js';

	interface Props {
		text: ISbRichtext | string;
	}

	let { text }: Props = $props();

	function isRichtext(content: string | ISbRichtext): content is ISbRichtext {
		return typeof content === 'object' && 'content' in content;
	}

	/**
	 * Usually, blocks will not be inlined and have their own line.
	 * We adjust the JSON structure manually to inline our custom blocks.
	 */
	function processRichtext(content: ISbRichtext): ISbRichtext | undefined {
		if (!content.content) return;

		const newContent = [];
		let i = 0;

		while (i < content.content.length) {
			const currentNode = content.content[i];
			const nextNode = content.content[i + 1];

			if (nextNode && nextNode.type === 'blok') {
				const followingNode = content.content[i + 2];

				if (followingNode && followingNode.type === currentNode.type) {
					// Merge the three nodes
					newContent.push({
						...currentNode,
						content: [...(currentNode.content || []), nextNode, ...(followingNode.content || [])]
					});
					i += 3; // Skip the next two nodes as we've merged them
				} else {
					// Merge only the current node and the blok
					newContent.push({
						...currentNode,
						content: [...(currentNode.content || []), nextNode]
					});
					i += 2; // Skip the next node as we've merged it
				}
			} else {
				newContent.push(currentNode);
				i++;
			}
		}

		return {
			...content,
			content: newContent
		};
	}

	let richtext = $derived(
		isRichtext(text)
			? renderRichText(processRichtext(text), {
					resolver: (component, blok) => {
						if (component === 'datasource_variable') {
							if (blok.variable.startsWith('=')) {
								return evaluate(blok.variable);
							}

							return blok.variable;
						}

						return '';
					}
				})
			: text
	);
</script>

{@html richtext}
